import { countries, CountryCode } from "@redotech/locale/countries";
import {
  createLazyContext,
  useLazyContext,
} from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import EditPencilIcon from "@redotech/redo-icons/edit-02.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import SlashCircleIcon from "@redotech/redo-icons/slash-circle-01.svg";
import DiscountIcon from "@redotech/redo-icons/tag-01.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import {
  RedoDiscount,
  RedoDiscountConfiguration,
} from "@redotech/redo-merchant-marketing-rpc/schema/discounts/discount-schemas";
import { getShopifySegments } from "@redotech/redo-merchant-marketing-rpc/schema/segments/get-shopify-segments";
import { SchemaType } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import {
  DiscountGroupType,
  DiscountMinRequirementType,
  DiscountValueType,
  ExpirationType,
} from "@redotech/redo-model/discount";
import {
  DiscountCategory,
  DiscountSettingsType,
  DynamicRangeExpirationUnits,
} from "@redotech/redo-model/discount/discount-db-parser";
import { formatDiscountDescription } from "@redotech/redo-model/discount/format-discount-description";
import { ProductInfo } from "@redotech/redo-model/product";
import {
  dateToCurrentPlainDate,
  plainDateToCurrentDate,
} from "@redotech/util/temporal";
import { assertNever } from "@redotech/util/type";
import Fuse from "fuse.js";
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type * as z from "zod/v4";
import { toast } from "../alert";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "../arbiter-components/checkbox/redo-checkbox";
import { RedoSingleDatePicker } from "../arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "../arbiter-components/input/redo-text-input";
import {
  BadgeRedoListItem,
  RedoListItem,
} from "../arbiter-components/list/redo-list";
import { RedoModal } from "../arbiter-components/modal/redo-modal";
import { RedoRadioGroup } from "../arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInput } from "../arbiter-components/select-dropdown/redo-tags-dropdown-input";
import {
  RedoTimePicker,
  TimeOfDay,
} from "../arbiter-components/time/redo-time-picker";
import { EmailBuilderContext } from "../email-builder/builder/email-builder-context";
import { Flex } from "../flex";
import { LabeledInput, LabelSize } from "../labeled-input";
import {
  ModalProduct,
  ProductSelectionModalEntry,
} from "../product/product-selection-modal";
import { Text } from "../text";
import { useDiscountForm } from "./hooks/use-discount-form";
import { validateDiscountConfiguration } from "./utils/discount-validation";

export type ShopifySegment = z.infer<typeof getShopifySegments.output>[number];
export interface ProductSelection {
  id: string;
  title: string;
}

export enum CustomerEligibility {
  ALL = "all",
  SEGMENTS = "segments",
  SPECIFIC = "specific",
}

export const INITIAL_CODE_NAME = "DISCOUNT";
export const INITIAL_START_TIME = { hours: 0, minutes: 0 } as const;
export const INITIAL_END_TIME = { hours: 23, minutes: 59 } as const;
export function getInitialSegments(
  existingDiscount: RedoDiscount | undefined,
  shopifySegments: ShopifySegment[] | undefined,
): ShopifySegment[] {
  if (existingDiscount?.discountSettings.limitToSegments && shopifySegments) {
    return shopifySegments.filter((segment) =>
      existingDiscount.discountSettings.limitToSegments?.includes(segment.id),
    );
  }
  return [];
}

export function combineDateTime(
  date: Date | undefined,
  time: TimeOfDay | null,
): Date | undefined {
  if (!date) return undefined;
  const combined = new Date(date);

  if (time) {
    combined.setHours(time.hours, time.minutes, 0, 0);
  } else {
    combined.setHours(0, 0, 0, 0);
  }

  return combined;
}

export interface DiscountMethodsContextI {
  deleteDiscount: (id: string) => Promise<void>;
  createDiscount: (args: {
    discountConfiguration: RedoDiscountConfiguration;
  }) => Promise<string>;
  updateDiscount: (args: {
    discountId: string;
    discountConfiguration: RedoDiscountConfiguration;
  }) => Promise<void>;
  searchProducts?: (params: {
    search: string;
    signal: AbortSignal;
  }) => Promise<ModalProduct[]>;
  searchCustomers?: (
    searchText: string,
    signal: AbortSignal,
  ) => Promise<
    {
      shopifyCustomerId?: string | null | undefined;
      email: string | null | undefined;
      first_name?: string | null;
      last_name?: string | null;
    }[]
  >;
}

const EmptyDiscountMethodsContext: DiscountMethodsContextI = {
  deleteDiscount: async () => {},
  createDiscount: async () => "",
  updateDiscount: async () => {},
};

export const CollectionsContext =
  createLazyContext<{ id: string; name: string }[]>();
export const DiscountsContext = createLazyContext<RedoDiscount[]>();
export const ProductsContext =
  createLazyContext<{ id: string; name: string }[]>();

export const DiscountMethodsContext = createContext<DiscountMethodsContextI>(
  EmptyDiscountMethodsContext,
);

export const ShopifySegmentsContext = createLazyContext<ShopifySegment[]>();

const NO_DISCOUNT_ID = "__no_discount__";

function formatDiscountSupportingText(discount: RedoDiscount): string {
  const code = discount.codeGenerationStrategy.code;
  const valueText = formatDiscountDescription({
    title: discount.name,
    discountSettings: discount.discountSettings,
  });
  return code ? `${code} · ${valueText}` : valueText;
}

function formatDiscountSelectedText(discount: RedoDiscount): string {
  const code = discount.codeGenerationStrategy.code;
  const valueText = formatDiscountDescription({
    title: discount.name,
    discountSettings: discount.discountSettings,
  });
  return code ? `${valueText} · ${code}` : valueText;
}

export const DiscountSelector = memo(function DiscountSelector({
  discountId,
  setDiscountId,
  required,
  hideCreateButton,
  excludeDiscountIds,
  showCreateAsDropdownOption,
  category,
  fitToAnchor,
  showLeadingIcon,
  showDiscountDetails,
  showNoDiscountOption,
}: {
  discountId: string | undefined;
  setDiscountId: (id: string | undefined) => void;
  required?: boolean;
  hideCreateButton?: boolean;
  excludeDiscountIds?: string[];
  showCreateAsDropdownOption?: boolean;
  category?: DiscountCategory;
  fitToAnchor?: boolean;
  showLeadingIcon?: boolean;
  showDiscountDetails?: boolean;
  showNoDiscountOption?: boolean;
}) {
  const emailBuilderContext = useContext(EmailBuilderContext);

  const isCampaignTemplate =
    emailBuilderContext?.template?.schemaType === SchemaType.MARKETING_CAMPAIGN;

  const { deleteDiscount } = useContext(DiscountMethodsContext);

  const [discounts] = useLazyContext(DiscountsContext);

  const [discountModalOpen, setDiscountModalOpen] = useState(false);

  const [discountBeingUpdated, setDiscountBeingUpdated] = useState<
    RedoDiscount | undefined
  >();

  const [noDiscountSelected, setNoDiscountSelected] = useState(
    showNoDiscountOption && !discountId,
  );

  const [searchString, setSearchString] = useState("");

  const options: RedoListItem<string>[] = useMemo(() => {
    if (!discounts.value) {
      return [];
    }

    let sortedDiscounts = [...discounts.value];

    // Filter by category
    if (category) {
      // If category is specified (e.g., RECOVER), only show discounts with that category
      sortedDiscounts = sortedDiscounts.filter(
        (discount) => discount.category === category,
      );
    } else {
      // If no category specified, only show discounts without a category (exclude RECOVER)
      sortedDiscounts = sortedDiscounts.filter(
        (discount) => !discount.category,
      );
    }

    if (excludeDiscountIds && excludeDiscountIds.length > 0) {
      sortedDiscounts = sortedDiscounts.filter(
        (discount) => !excludeDiscountIds.includes(discount._id),
      );
    }

    if (searchString) {
      const lowerSearch = searchString.toLowerCase();
      sortedDiscounts = sortedDiscounts.filter((discount) =>
        discount.name.toLowerCase().includes(lowerSearch),
      );
    }

    if (isCampaignTemplate) {
      sortedDiscounts = sortedDiscounts.sort((a, b) => {
        const aIsDynamic =
          a.codeGenerationStrategy.strategy === DiscountGroupType.DYNAMIC;
        const bIsDynamic =
          b.codeGenerationStrategy.strategy === DiscountGroupType.DYNAMIC;
        if (aIsDynamic === bIsDynamic) return 0;
        return aIsDynamic ? 1 : -1;
      });
    }

    const discountOptions: RedoListItem<string>[] = sortedDiscounts.map(
      (discount) => ({
        id: discount._id,
        type: "text" as const,
        text: discount.name,
        value: discount._id,
        ...(showDiscountDetails && {
          supportingText: formatDiscountSupportingText(discount),
        }),
        menu: [
          {
            onClick: async () => {
              setDiscountBeingUpdated(discount);
              setDiscountModalOpen(true);
            },
            text: "Edit",
            Icon: EditPencilIcon,
          },
          {
            onClick: async () => {
              await deleteDiscount(discount._id);
              setDiscountId(undefined);
            },
            text: "Delete",
            Icon: TrashIcon,
            theme: "destructive" as const,
          },
        ],
      }),
    );

    const noDiscountOption: RedoListItem<string>[] = showNoDiscountOption
      ? [
          {
            id: NO_DISCOUNT_ID,
            type: "text" as const,
            text: "No discount",
            value: NO_DISCOUNT_ID,
          },
        ]
      : [];

    if (showCreateAsDropdownOption) {
      return [
        ...noDiscountOption,
        ...discountOptions,
        ...(discountOptions.length > 0
          ? [
              {
                id: "divider-section",
                type: "custom" as const,
                value: "section",
                useWrapper: false,
                render: (
                  <div
                    style={{
                      height: "1px",
                      width: "100%",
                      backgroundColor: "#e5e7eb",
                    }}
                  />
                ),
              },
            ]
          : []),
        {
          id: "create-discount",
          type: "text" as const,
          text: "Create Discount",
          value: "create-discount",
          leadingItem: { type: "icon" as const, Icon: PlusIcon },
        },
      ];
    }

    return [...noDiscountOption, ...discountOptions];
  }, [
    discounts.value,
    setDiscountId,
    deleteDiscount,
    isCampaignTemplate,
    searchString,
    excludeDiscountIds,
    showCreateAsDropdownOption,
    category,
    showDiscountDetails,
    showNoDiscountOption,
  ]);

  const selectedItem = useMemo((): RedoListItem<string> | undefined => {
    const resolvedId = noDiscountSelected ? NO_DISCOUNT_ID : discountId;
    const option = options.find((o) => o.id === resolvedId);
    if (!option) {
      return undefined;
    }

    if (!showDiscountDetails && !showLeadingIcon) {
      return option;
    }

    if (option.id === NO_DISCOUNT_ID) {
      return {
        ...option,
        ...(showLeadingIcon && {
          leadingItem: { type: "icon" as const, Icon: SlashCircleIcon },
        }),
      };
    }

    const discount = discounts.value?.find((d) => d._id === option.id);
    if (!discount) {
      return option;
    }

    return {
      ...option,
      ...(showDiscountDetails && {
        text: formatDiscountSelectedText(discount),
        supportingText: undefined,
      }),
      ...(showLeadingIcon && {
        leadingItem: { type: "icon" as const, Icon: DiscountIcon },
      }),
    };
  }, [
    discountId,
    noDiscountSelected,
    options,
    discounts.value,
    showDiscountDetails,
    showLeadingIcon,
  ]);

  const dynamicDiscountIds = useMemo(() => {
    if (!isCampaignTemplate || !discounts.value) {
      return [];
    }
    return discounts.value
      .filter(
        (discount) =>
          discount.codeGenerationStrategy.strategy ===
          DiscountGroupType.DYNAMIC,
      )
      .map((discount) => discount._id);
  }, [isCampaignTemplate, discounts.value]);

  const disabledTooltips = useMemo(() => {
    const tooltips: Record<string, string> = {};
    for (const id of dynamicDiscountIds) {
      tooltips[id] = "Dynamic discounts cannot be used within campaigns";
    }
    return tooltips;
  }, [dynamicDiscountIds]);

  return (
    <Flex dir="column" gap="sm">
      <RedoSingleSelectDropdownInput
        disabled={discounts.value?.length === 0 && !showCreateAsDropdownOption}
        disabledItems={isCampaignTemplate ? dynamicDiscountIds : undefined}
        disabledTooltips={isCampaignTemplate ? disabledTooltips : undefined}
        fitToAnchor={fitToAnchor}
        itemsLoading={discounts.pending}
        options={options}
        optionSelected={({ item }) => {
          if (item.value === "create-discount") {
            setDiscountModalOpen(true);
          } else if (item.value === NO_DISCOUNT_ID) {
            setNoDiscountSelected(true);
            setDiscountId(undefined);
          } else {
            setNoDiscountSelected(false);
            setDiscountId(item.value);
          }
        }}
        placeholder="Select a discount"
        searchPlaceholder="Search discounts"
        searchString={searchString}
        selectedItem={selectedItem}
        setSearchString={setSearchString}
        size="xs"
      />
      {required && !discountId && (
        <Text fontSize="xs" textColor="error">
          Discount is required
        </Text>
      )}
      {!hideCreateButton && (
        <RedoButton
          hierarchy="secondary"
          onClick={() => {
            setDiscountModalOpen(true);
          }}
          size="sm"
          text={`${category === DiscountCategory.RECOVER ? "Create Recover discount" : "Create discount"}`}
        />
      )}
      <DiscountModal
        category={category}
        existingDiscount={discountBeingUpdated}
        isCampaignTemplate={isCampaignTemplate}
        isOpen={discountModalOpen}
        key={`${discountModalOpen}`}
        onModalCloseRequested={() => {
          setDiscountBeingUpdated(undefined);
          setDiscountModalOpen(false);
        }}
        value={discountId}
        valueChange={(value) => {
          setDiscountId(value);
        }}
      />
    </Flex>
  );
});

const CustomerSearchDropdown = memo(function CustomerSearchDropdown({
  selectedCustomerIds,
  setSelectedCustomerIds,
}: {
  selectedCustomerIds: string[];
  setSelectedCustomerIds: (customerIds: string[]) => void;
}) {
  const { searchCustomers } = useContext(DiscountMethodsContext);
  const [searchText, setSearchText] = useState("");

  const memoizedCustomerSearch = useMemo(() => {
    if (!searchCustomers) return undefined;

    return (searchTerm: string, abortSignal: AbortSignal) =>
      searchCustomers(searchTerm, abortSignal);
  }, [searchCustomers]);

  const [customerSearchResults, setCustomerSearchResults] = useState<{
    results: any[];
    loading: boolean;
  }>({ results: [], loading: false });

  useEffect(() => {
    if (!memoizedCustomerSearch || !searchText.trim()) {
      setCustomerSearchResults({ results: [], loading: false });
      return;
    }

    const abortController = new AbortController();
    const timeoutId = setTimeout(async () => {
      setCustomerSearchResults({ results: [], loading: true });

      try {
        const customers = await memoizedCustomerSearch(
          searchText,
          abortController.signal,
        );
        if (!abortController.signal.aborted) {
          setCustomerSearchResults({
            results: customers || [],
            loading: false,
          });
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error("Customer search failed:", error);
          setCustomerSearchResults({ results: [], loading: false });
        }
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [memoizedCustomerSearch, searchText]);

  // Enrich customer data once with display names
  const enrichedCustomers = useMemo(() => {
    return (customerSearchResults.results || []).map((customer) => ({
      ...customer,
      displayName: !customer
        ? ""
        : !customer.first_name && !customer.last_name && customer.email
          ? `Unknown customer - ${customer.email}`
          : `${[customer.first_name, customer.last_name].filter(Boolean).join(" ")} - ${customer.email}`,
    }));
  }, [customerSearchResults.results]);

  const customerOptions: BadgeRedoListItem<{
    shopifyCustomerId: string;
    name: string;
  }>[] = useMemo(() => {
    return enrichedCustomers
      .filter((customer) => customer.shopifyCustomerId)
      .map((customer) => ({
        id: customer.shopifyCustomerId!,
        value: {
          shopifyCustomerId: customer.shopifyCustomerId!,
          name: customer.displayName,
        },
        type: "badge" as const,
        badge: { text: customer.displayName },
      }));
  }, [enrichedCustomers]);

  const selectedCustomerOptions: BadgeRedoListItem<{
    shopifyCustomerId: string;
    name: string;
  }>[] = useMemo(() => {
    return selectedCustomerIds.map((shopifyCustomerId) => {
      const customer = enrichedCustomers.find(
        (c) => c.shopifyCustomerId === shopifyCustomerId,
      );
      const displayName =
        customer?.displayName || `Customer ID: ${shopifyCustomerId}`;

      return {
        id: shopifyCustomerId,
        value: { shopifyCustomerId, name: displayName },
        type: "badge" as const,
        badge: { text: displayName },
      };
    });
  }, [selectedCustomerIds, enrichedCustomers]);

  const handleCustomerChange = useCallback(
    (
      items: BadgeRedoListItem<{ shopifyCustomerId: string; name: string }>[],
    ) => {
      setSelectedCustomerIds(items.map((item) => item.value.shopifyCustomerId));
    },
    [setSelectedCustomerIds],
  );

  return (
    <RedoTagsDropdownInput
      loading={customerSearchResults.loading}
      options={customerOptions}
      placeholder="Search and select customers"
      searchPlaceholder="Search customers by name, email, phone, or order number"
      searchString={searchText}
      selectedOptions={selectedCustomerOptions}
      setSearchString={setSearchText}
      setSelectedOptions={handleCustomerChange}
      size="xs"
    />
  );
});

const ProductsPicker = memo(function ProductsPicker({
  products,
  setProducts,
}: {
  products: ProductSelection[];
  setProducts: (p: ProductSelection[]) => void;
}) {
  const { searchProducts } = useContext(DiscountMethodsContext);
  const emailBuilderContext = useContext(EmailBuilderContext);

  const providers = emailBuilderContext?.useTeamProviders().data;

  const selectedProductsDisplay = useMemo(
    () => products.map((product) => ({ id: product.id, name: product.title })),
    [products],
  );

  const finalSearchProducts = useMemo(() => {
    if (searchProducts) {
      return searchProducts;
    }

    if (emailBuilderContext?.searchProducts && providers?.default) {
      return async ({
        search,
        signal,
      }: {
        search: string;
        signal: AbortSignal;
      }): Promise<ModalProduct[]> => {
        try {
          const products = await emailBuilderContext.searchProducts({
            search,
            provider: providers?.default,
            signal,
          });
          if (!products) return [];
          return products.map(convertProductInfoToModalProduct);
        } catch (error) {
          return [];
        }
      };
    }

    return undefined;
  }, [searchProducts, emailBuilderContext, providers]);

  const handleProductSelectionChange = useCallback(
    (selectedItems: { productId: string; productTitle?: string }[]) => {
      const newProductSelections: ProductSelection[] = selectedItems.map(
        (item) => ({
          id: item.productId,
          title:
            item.productTitle ||
            `Product ${item.productId.includes("gid://shopify/Product/") ? item.productId.split("/").pop() : item.productId}`,
        }),
      );

      const existingIds = new Set(products.map((p) => p.id));
      const uniqueNewProducts = newProductSelections.filter(
        (p) => !existingIds.has(p.id),
      );

      setProducts([...products, ...uniqueNewProducts]);
    },
    [products, setProducts],
  );

  return (
    <Flex dir="column" gap="sm">
      {selectedProductsDisplay.length > 0 ? (
        <Flex dir="column" gap="xs">
          {selectedProductsDisplay.map((product) => (
            <Flex
              align="center"
              gap="xs"
              key={product.id}
              p="sm"
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
                minHeight: "40px",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Text fontSize="sm">{product.name}</Text>
              <RedoButton
                hierarchy="tertiary"
                IconLeading={TrashIcon}
                onClick={() =>
                  setProducts(products.filter((p) => p.id !== product.id))
                }
                size="xs"
              />
            </Flex>
          ))}
        </Flex>
      ) : (
        <Text fontSize="xs" textColor="secondary">
          No products selected
        </Text>
      )}

      {finalSearchProducts ? (
        <ProductSelectionModalEntry
          customAddProductElement={({ onAdd, disabled }) => (
            <RedoButton
              disabled={disabled}
              hierarchy="secondary"
              onClick={onAdd}
              size="sm"
              text="Select products"
            />
          )}
          multiselect
          productSelectionChange={handleProductSelectionChange}
          searchProducts={finalSearchProducts}
        />
      ) : (
        <Text fontSize="sm" textColor="secondary">
          Product search not available
        </Text>
      )}
    </Flex>
  );
});

export const DiscountModal = memo(function DiscountModal({
  value: _value,
  valueChange,
  isCampaignTemplate,
  isOpen,
  onModalCloseRequested,
  existingDiscount,
  category,
  allowedDiscountTypes,
  onConfigurationReady,
  initialDynamicRangeConfig,
}: {
  value?: string | undefined;
  valueChange?: (value: string) => void;
  isCampaignTemplate?: boolean;
  isOpen: boolean;
  onModalCloseRequested: () => void;
  existingDiscount?: RedoDiscount | undefined;
  category?: DiscountCategory;
  allowedDiscountTypes?: DiscountSettingsType[];
  onConfigurationReady?: (config: RedoDiscountConfiguration) => void;
  initialDynamicRangeConfig?: {
    discountName?: string;
    discountCodePrefix: string;
    minPercentage: number;
    maxPercentage: number;
    expirationValue: number;
    expirationUnits: DynamicRangeExpirationUnits;
    combinesWith?: {
      orderDiscounts: boolean;
      productDiscounts: boolean;
      shippingDiscount: boolean;
    };
    minimumRequirement?: { type: DiscountMinRequirementType; minimum: number };
    customerEligibility?: CustomerEligibility;
    totalUsageLimit?: number;
    appliesOncePerCustomer?: boolean;
  };
}) {
  const { updateDiscount, createDiscount } = useContext(DiscountMethodsContext);
  const [showHiddenRecoverFields, setShowHiddenRecoverFields] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const formState = useDiscountForm(existingDiscount, category);
  const {
    // State values
    discountName,
    setDiscountName,
    codeGenerationStrategy,
    setCodeGenerationStrategy,
    codeStrategyName,
    setCodeStrategyName,
    discountAmount,
    setDiscountAmount,
    discountValueType,
    setDiscountValueType,
    combineDiscountWith,
    setCombineDiscountWith,
    excludeOnSale,
    specifyCollections,
    specifyProducts,
    selectedProducts,
    setSelectedProducts,
    collectionIds,
    setCollectionIds,
    expiration,
    setExpiration,
    minimumQuantity,
    minimumSubtotal,
    customerEligibility,
    setCustomerEligibility,
    selectedCustomerIds,
    setSelectedCustomerIdsString,
    totalUsageLimit,
    setTotalUsageLimit,
    appliesOncePerCustomer,
    setAppliesOncePerCustomer,
    userSelectedShopifySegments,
    startDate,
    setStartDate,
    startTime,
    setStartTime,

    // Discount settings type
    discountSettingsType,
    setDiscountSettingsType,

    // Free Shipping fields
    maximumShippingPrice,
    setMaximumShippingPrice,
    freeShippingCountryCodes,
    setFreeShippingCountryCodes,

    // BxGy fields - Customer Buys
    bxgyTriggerType,
    setBxgyTriggerType,
    bxgyTriggerQuantity,
    setBxgyTriggerQuantity,
    bxgyTriggerAmount,
    setBxgyTriggerAmount,
    bxgyTriggerItemType,
    setBxgyTriggerItemType,
    bxgyTriggerProducts,
    setBxgyTriggerProducts,
    bxgyTriggerCollections,
    setBxgyTriggerCollections,

    // BxGy fields - Customer Gets
    bxgyRewardItemType,
    setBxgyRewardItemType,
    bxgyRewardProducts,
    setBxgyRewardProducts,
    bxgyRewardCollections,
    setBxgyRewardCollections,
    bxgyRewardQuantity,
    setBxgyRewardQuantity,
    bxgyRewardDiscountType,
    setBxgyRewardDiscountType,
    bxgyRewardDiscountValue,
    setBxgyRewardDiscountValue,
    bxgyUsesPerOrderLimit,
    setBxgyUsesPerOrderLimit,

    // Dynamic Range fields
    dynamicRangeMinPercentage,
    setDynamicRangeMinPercentage,
    dynamicRangeMaxPercentage,
    setDynamicRangeMaxPercentage,
    dynamicRangeExpirationUnits,
    setDynamicRangeExpirationUnits,
    dynamicRangeExpirationValue,
    setDynamicRangeExpirationValue,

    // Computed values
    productIds,
    fullStartDateTime,
    ShopifySegmentOptions,
    selectedShopifySegmentOptions,

    // Handlers
    handleSetExcludeOnSale,
    handleSetSpecifyCollections,
    handleSetSpecifyProducts,
    handleShopifySegmentChange,
    handleSetMinimumQuantity,
    handleSetMinimumSubtotal,

    // Context data
    discounts,

    // Constants
    CustomerEligibility,
    DiscountSettingsType,
    BxgyTriggerType,
  } = formState;

  // Pre-populate from initialDynamicRangeConfig when modal opens
  useEffect(() => {
    if (!isOpen || !initialDynamicRangeConfig || initialized) {
      return;
    }
    setDiscountSettingsType(DiscountSettingsType.DYNAMIC_RANGE);
    setCodeGenerationStrategy(DiscountGroupType.DYNAMIC);
    setCodeStrategyName(initialDynamicRangeConfig.discountCodePrefix);
    setDiscountName(
      initialDynamicRangeConfig.discountName || "Marketing Agent Discount",
    );
    setDynamicRangeMinPercentage(initialDynamicRangeConfig.minPercentage);
    setDynamicRangeMaxPercentage(initialDynamicRangeConfig.maxPercentage);
    setDynamicRangeExpirationValue(initialDynamicRangeConfig.expirationValue);
    setDynamicRangeExpirationUnits(initialDynamicRangeConfig.expirationUnits);

    // Pre-populate shared fields
    if (initialDynamicRangeConfig.combinesWith) {
      setCombineDiscountWith(initialDynamicRangeConfig.combinesWith);
    }
    if (initialDynamicRangeConfig.minimumRequirement) {
      const req = initialDynamicRangeConfig.minimumRequirement;
      if (req.type === DiscountMinRequirementType.MINIMUM_QUANTITY) {
        handleSetMinimumQuantity(req.minimum);
      } else if (req.type === DiscountMinRequirementType.MINIMUM_SUBTOTAL) {
        handleSetMinimumSubtotal(req.minimum);
      }
    }
    if (initialDynamicRangeConfig.customerEligibility) {
      setCustomerEligibility(initialDynamicRangeConfig.customerEligibility);
    }
    if (initialDynamicRangeConfig.totalUsageLimit) {
      setTotalUsageLimit(initialDynamicRangeConfig.totalUsageLimit);
    }
    if (initialDynamicRangeConfig.appliesOncePerCustomer !== undefined) {
      setAppliesOncePerCustomer(
        initialDynamicRangeConfig.appliesOncePerCustomer,
      );
    }

    setInitialized(true);
  }, [
    isOpen,
    initialDynamicRangeConfig,
    initialized,
    setDiscountSettingsType,
    DiscountSettingsType,
    setCodeGenerationStrategy,
    setCodeStrategyName,
    setDiscountName,
    setDynamicRangeMinPercentage,
    setDynamicRangeMaxPercentage,
    setDynamicRangeExpirationValue,
    setDynamicRangeExpirationUnits,
    setCombineDiscountWith,
    handleSetMinimumQuantity,
    handleSetMinimumSubtotal,
    setCustomerEligibility,
    setTotalUsageLimit,
    setAppliesOncePerCustomer,
  ]);

  const discountValidationResult: RedoDiscountConfiguration | string =
    useMemo(() => {
      return validateDiscountConfiguration({
        discountName,
        discountAmount,
        discountValueType,
        codeGenerationStrategy,
        codeStrategyName,
        combineDiscountWith,
        excludeOnSale,
        specifyCollections,
        specifyProducts,
        collectionIds,
        productIds,
        expiration,
        minimumQuantity,
        minimumSubtotal,
        customerEligibility,
        selectedCustomerIds,
        limitToSegments: userSelectedShopifySegments.map(
          (segment) => segment.id,
        ),
        totalUsageLimit,
        appliesOncePerCustomer,
        fullStartDateTime,
        existingDiscount,
        existingDiscounts: discounts.value,
        category,
        // Discount settings type
        discountSettingsType,
        // Free Shipping fields
        maximumShippingPrice,
        freeShippingCountryCodes,
        // BxGy fields
        bxgyTriggerType,
        bxgyTriggerQuantity,
        bxgyTriggerAmount,
        bxgyTriggerItemType,
        bxgyTriggerProducts,
        bxgyTriggerCollections,
        bxgyRewardItemType,
        bxgyRewardProducts,
        bxgyRewardCollections,
        bxgyRewardQuantity,
        bxgyRewardDiscountType,
        bxgyRewardDiscountValue,
        bxgyUsesPerOrderLimit,
        dynamicRangeMinPercentage,
        dynamicRangeMaxPercentage,
        dynamicRangeExpirationUnits,
        dynamicRangeExpirationValue,
      });
    }, [
      discountName,
      discountAmount,
      discountValueType,
      codeGenerationStrategy,
      codeStrategyName,
      combineDiscountWith,
      excludeOnSale,
      specifyCollections,
      specifyProducts,
      collectionIds,
      productIds,
      expiration,
      minimumQuantity,
      minimumSubtotal,
      customerEligibility,
      selectedCustomerIds,
      userSelectedShopifySegments,
      totalUsageLimit,
      appliesOncePerCustomer,
      fullStartDateTime,
      existingDiscount,
      discounts.value,
      category,
      discountSettingsType,
      maximumShippingPrice,
      freeShippingCountryCodes,
      bxgyTriggerType,
      bxgyTriggerQuantity,
      bxgyTriggerAmount,
      bxgyTriggerItemType,
      bxgyTriggerProducts,
      bxgyTriggerCollections,
      bxgyRewardItemType,
      bxgyRewardProducts,
      bxgyRewardCollections,
      bxgyRewardQuantity,
      bxgyRewardDiscountType,
      bxgyRewardDiscountValue,
      bxgyUsesPerOrderLimit,
      dynamicRangeMinPercentage,
      dynamicRangeMaxPercentage,
      dynamicRangeExpirationUnits,
      dynamicRangeExpirationValue,
    ]);

  const initialConfigRef = useRef<string | null>(null);
  if (
    initialConfigRef.current === null &&
    existingDiscount &&
    typeof discountValidationResult !== "string"
  ) {
    initialConfigRef.current = JSON.stringify(discountValidationResult);
  }

  const isDirty = existingDiscount
    ? typeof discountValidationResult !== "string" &&
      initialConfigRef.current !== null &&
      JSON.stringify(discountValidationResult) !== initialConfigRef.current
    : true;

  const handleCreateOrUpdateDiscount = useHandler(async () => {
    if (typeof discountValidationResult === "string") {
      return;
    }

    if (onConfigurationReady) {
      onConfigurationReady(discountValidationResult);
      onModalCloseRequested();
      return;
    }

    try {
      if (existingDiscount) {
        await updateDiscount({
          discountId: existingDiscount._id,
          discountConfiguration: discountValidationResult,
        });
      } else {
        const discountId = await createDiscount({
          discountConfiguration: discountValidationResult,
        });
        valueChange?.(discountId);
      }
      onModalCloseRequested();
    } catch (e: unknown) {
      if (e instanceof Error) {
        toast(e.message, { variant: "error" });
      }
    }
  });

  const filteredDiscountSettingsTypeOptions: RedoListItem<DiscountSettingsType>[] =
    useMemo(() => {
      const labels: Record<DiscountSettingsType, string> = {
        [DiscountSettingsType.SHOPIFY_BASIC]: "Percentage or amount off",
        [DiscountSettingsType.SHOPIFY_FREE_SHIPPING]: "Free shipping",
        [DiscountSettingsType.SHOPIFY_BXGY]: "Buy X Get Y",
        [DiscountSettingsType.DYNAMIC_RANGE]: "Dynamic range",
      };
      const types = allowedDiscountTypes ?? [
        DiscountSettingsType.SHOPIFY_BASIC,
        DiscountSettingsType.SHOPIFY_FREE_SHIPPING,
        DiscountSettingsType.SHOPIFY_BXGY,
      ];
      return types.map((type) => ({
        id: type,
        type: "text" as const,
        value: type,
        text: labels[type],
      }));
    }, [allowedDiscountTypes, DiscountSettingsType]);

  // Auto-set type when only one option is allowed
  useEffect(() => {
    const firstOption = filteredDiscountSettingsTypeOptions[0];
    if (filteredDiscountSettingsTypeOptions.length === 1 && firstOption) {
      setDiscountSettingsType(firstOption.value);
    }
  }, [filteredDiscountSettingsTypeOptions, setDiscountSettingsType]);

  const isDynamicRange =
    discountSettingsType === DiscountSettingsType.DYNAMIC_RANGE;

  return (
    <RedoModal
      footerBorder
      headerBorder
      headerIconAlignment="inline"
      isOpen={isOpen}
      modalSize="md"
      onModalCloseRequested={onModalCloseRequested}
      primaryButton={{
        text: onConfigurationReady
          ? "Save configuration"
          : existingDiscount
            ? "Update discount"
            : "Create discount",
        disabled:
          typeof discountValidationResult === "string"
            ? discountValidationResult
            : !isDirty,
        onClick: handleCreateOrUpdateDiscount,
        size: "sm",
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: () => {
          onModalCloseRequested();
        },
        size: "sm",
      }}
      subtitle={
        existingDiscount
          ? "Updating your discount may be disruptive for audience members who have already received it"
          : undefined
      }
      theme={existingDiscount ? "warn" : undefined}
      title={
        onConfigurationReady
          ? "Configure discount"
          : existingDiscount
            ? "Update discount"
            : "Create discount"
      }
      TitleIcon={DiscountIcon}
    >
      <Flex dir="column" gap="xl">
        <RedoTextInput
          label="Name"
          placeholder="Add a name"
          setValue={setDiscountName}
          value={discountName}
        />
        {/* Discount Type Selector */}
        {!existingDiscount &&
          filteredDiscountSettingsTypeOptions.length > 1 && (
            <RedoSingleSelectDropdownInput<DiscountSettingsType>
              label="Discount type"
              options={filteredDiscountSettingsTypeOptions}
              optionSelected={({ item }) => {
                setDiscountSettingsType(item.value);
              }}
              selectedItem={filteredDiscountSettingsTypeOptions.find(
                (o) => o.value === discountSettingsType,
              )}
              size="xs"
            />
          )}
        {category !== DiscountCategory.RECOVER && !isDynamicRange && (
          <RedoSingleSelectDropdownInput<DiscountGroupType>
            description={getDiscountCodeStrategyDescription({
              prefix: codeStrategyName,
              type: codeGenerationStrategy,
            })}
            disabled={!!existingDiscount}
            disabledItems={
              isCampaignTemplate ? [DiscountGroupType.DYNAMIC] : undefined
            }
            disabledTooltips={
              isCampaignTemplate
                ? {
                    [DiscountGroupType.DYNAMIC]:
                      "Dynamic discounts cannot be used within campaigns",
                  }
                : undefined
            }
            label="Code generation strategy"
            options={discountGroupTypeOptions}
            optionSelected={({ item }) => {
              setCodeGenerationStrategy(item.value);
            }}
            placeholder="Select a discount type"
            selectedItem={discountGroupTypeOptions.find(
              (o) => o.id === codeGenerationStrategy,
            )}
            size="xs"
          />
        )}
        <RedoTextInput
          fullWidth
          label={discountCodeNameTitle[codeGenerationStrategy]}
          placeholder="Name your code"
          setValue={setCodeStrategyName}
          state={existingDiscount ? "disabled" : "default"}
          value={codeStrategyName}
        />
        {/* Discount amount - for SHOPIFY_BASIC only */}
        {discountSettingsType === DiscountSettingsType.SHOPIFY_BASIC && (
          <RedoTextInput
            inputTypeHint="number"
            label="Discount amount"
            placeholder="Add a discount amount"
            setValue={(value) => {
              setDiscountAmount(Number(value));
            }}
            trailingActions={
              <Flex>
                <RedoSingleSelectDropdownInput<DiscountValueType>
                  fitToAnchor={false}
                  hideBorder
                  options={discountValueTypeItems}
                  optionSelected={({ item }) => {
                    setDiscountValueType(item.value);
                  }}
                  selectedItem={discountValueTypeItems.find(
                    (o) => o.value === discountValueType,
                  )}
                  size="xs"
                />
              </Flex>
            }
            value={
              discountAmount !== undefined
                ? discountAmount % 1 === 0
                  ? discountAmount.toString()
                  : discountAmount.toFixed(2).replace(/\.?0+$/, "")
                : ""
            }
          />
        )}
        {/* Free Shipping fields */}
        {discountSettingsType ===
          DiscountSettingsType.SHOPIFY_FREE_SHIPPING && (
          <>
            <RedoTextInput
              inputTypeHint="number"
              label="Maximum shipping price to cover (optional)"
              placeholder="Leave empty to cover all shipping costs"
              setValue={(value) => {
                setMaximumShippingPrice(value ? Number(value) : undefined);
              }}
              value={maximumShippingPrice?.toString() || ""}
            />
            <LabeledInput
              label={
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Shipping destination countries (optional)
                </Text>
              }
              size={LabelSize.EXTRA_SMALL}
            >
              <CountriesPicker
                countryCodes={freeShippingCountryCodes}
                setCountryCodes={setFreeShippingCountryCodes}
              />
            </LabeledInput>
          </>
        )}
        {/* Buy X Get Y fields */}
        {discountSettingsType === DiscountSettingsType.SHOPIFY_BXGY && (
          <>
            <LabeledInput
              label={
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Customer buys
                </Text>
              }
              size={LabelSize.EXTRA_SMALL}
            >
              <Flex dir="column" gap="md">
                <RedoRadioGroup
                  options={[
                    {
                      id: BxgyTriggerType.MINIMUM_QUANTITY,
                      label: "Minimum quantity",
                    },
                    {
                      id: BxgyTriggerType.MINIMUM_AMOUNT,
                      label: "Minimum purchase amount",
                    },
                  ]}
                  setValue={(value) =>
                    setBxgyTriggerType(
                      value as
                        | typeof BxgyTriggerType.MINIMUM_QUANTITY
                        | typeof BxgyTriggerType.MINIMUM_AMOUNT,
                    )
                  }
                  value={bxgyTriggerType}
                />
                {bxgyTriggerType === BxgyTriggerType.MINIMUM_QUANTITY && (
                  <RedoTextInput
                    inputTypeHint="number"
                    label="Quantity"
                    placeholder="1"
                    setValue={(value) => {
                      setBxgyTriggerQuantity(value ? Number(value) : undefined);
                    }}
                    value={bxgyTriggerQuantity?.toString() || ""}
                  />
                )}
                {bxgyTriggerType === BxgyTriggerType.MINIMUM_AMOUNT && (
                  <RedoTextInput
                    inputTypeHint="number"
                    label="Amount ($)"
                    placeholder="0"
                    setValue={(value) => {
                      setBxgyTriggerAmount(value ? Number(value) : undefined);
                    }}
                    value={bxgyTriggerAmount?.toString() || ""}
                  />
                )}
                <Flex dir="column" gap="xs">
                  <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                    From
                  </Text>
                  <RedoRadioGroup
                    options={[
                      { id: "products", label: "Specific products" },
                      { id: "collections", label: "Collections" },
                    ]}
                    setValue={(value) =>
                      setBxgyTriggerItemType(
                        value as "products" | "collections",
                      )
                    }
                    value={bxgyTriggerItemType}
                  />
                  {bxgyTriggerItemType === "products" && (
                    <ProductsPicker
                      products={bxgyTriggerProducts}
                      setProducts={setBxgyTriggerProducts}
                    />
                  )}
                  {bxgyTriggerItemType === "collections" && (
                    <CollectionsPicker
                      collections={bxgyTriggerCollections}
                      setCollections={setBxgyTriggerCollections}
                    />
                  )}
                </Flex>
              </Flex>
            </LabeledInput>
            <LabeledInput
              label={
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Customer gets
                </Text>
              }
              size={LabelSize.EXTRA_SMALL}
            >
              <Flex dir="column" gap="md">
                <Flex dir="column" gap="xs">
                  <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                    From
                  </Text>
                  <RedoRadioGroup
                    options={[
                      { id: "products", label: "Specific products" },
                      { id: "collections", label: "Collections" },
                    ]}
                    setValue={(value) =>
                      setBxgyRewardItemType(value as "products" | "collections")
                    }
                    value={bxgyRewardItemType}
                  />
                  {bxgyRewardItemType === "products" && (
                    <ProductsPicker
                      products={bxgyRewardProducts}
                      setProducts={setBxgyRewardProducts}
                    />
                  )}
                  {bxgyRewardItemType === "collections" && (
                    <CollectionsPicker
                      collections={bxgyRewardCollections}
                      setCollections={setBxgyRewardCollections}
                    />
                  )}
                </Flex>
                <RedoTextInput
                  inputTypeHint="number"
                  label="Quantity"
                  placeholder="1"
                  setValue={(value) => {
                    setBxgyRewardQuantity(value ? Number(value) : 1);
                  }}
                  value={bxgyRewardQuantity?.toString() || "1"}
                />
                <Flex align="center" gap="md">
                  <RedoTextInput
                    inputTypeHint="number"
                    label="Discount"
                    placeholder="100"
                    setValue={(value) => {
                      setBxgyRewardDiscountValue(value ? Number(value) : 100);
                    }}
                    value={bxgyRewardDiscountValue?.toString() || "100"}
                  />
                  <RedoSingleSelectDropdownInput<DiscountValueType>
                    label="Type"
                    options={discountValueTypeItems}
                    optionSelected={({ item }) => {
                      setBxgyRewardDiscountType(item.value);
                    }}
                    selectedItem={discountValueTypeItems.find(
                      (o) => o.value === bxgyRewardDiscountType,
                    )}
                    size="xs"
                  />
                </Flex>
              </Flex>
            </LabeledInput>
            <RedoTextInput
              inputTypeHint="number"
              label="Maximum uses per order"
              placeholder="Unlimited"
              setValue={(value) => {
                setBxgyUsesPerOrderLimit(value ? Number(value) : undefined);
              }}
              value={bxgyUsesPerOrderLimit?.toString() || ""}
            />
          </>
        )}
        {/* Dynamic Range fields */}
        {isDynamicRange && (
          <>
            <Flex align="center" gap="md">
              <RedoTextInput
                inputTypeHint="number"
                label="Min percentage"
                placeholder="0"
                setValue={(value) => {
                  setDynamicRangeMinPercentage(
                    value ? Number(value) : undefined,
                  );
                }}
                value={dynamicRangeMinPercentage?.toString() ?? ""}
              />
              <RedoTextInput
                inputTypeHint="number"
                label="Max percentage"
                placeholder="100"
                setValue={(value) => {
                  setDynamicRangeMaxPercentage(
                    value ? Number(value) : undefined,
                  );
                }}
                value={dynamicRangeMaxPercentage?.toString() ?? ""}
              />
            </Flex>
            <Flex align="center" gap="md">
              <RedoTextInput
                inputTypeHint="number"
                label="Expiration value"
                min={1}
                placeholder="1"
                setValue={(value) => {
                  setDynamicRangeExpirationValue(
                    value ? Number(value) : undefined,
                  );
                }}
                value={dynamicRangeExpirationValue?.toString() ?? ""}
              />
              <RedoSingleSelectDropdownInput<DynamicRangeExpirationUnits>
                label="Expiration units"
                options={dynamicRangeExpirationUnitsOptions}
                optionSelected={({ item }) => {
                  setDynamicRangeExpirationUnits(item.value);
                }}
                selectedItem={dynamicRangeExpirationUnitsOptions.find(
                  (o) => o.value === dynamicRangeExpirationUnits,
                )}
                size="xs"
              />
            </Flex>
          </>
        )}
        <LabeledInput
          label={
            <Text fontWeight="medium" textColor="secondary">
              Combine discount with
            </Text>
          }
          size={LabelSize.EXTRA_SMALL}
        >
          <Flex dir="column" gap="md">
            {/* Hide "Product discounts" option for BXGY discounts - BXGY IS a product discount and can't combine with other product discounts on the same items */}
            {discountSettingsType !== DiscountSettingsType.SHOPIFY_BXGY && (
              <RedoCheckbox
                label="Product discounts"
                setValue={() => {
                  setCombineDiscountWith({
                    ...combineDiscountWith,
                    productDiscounts: !combineDiscountWith.productDiscounts,
                  });
                }}
                size="xs"
                value={combineDiscountWith.productDiscounts}
              />
            )}
            <RedoCheckbox
              label="Order discounts"
              setValue={() => {
                setCombineDiscountWith({
                  ...combineDiscountWith,
                  orderDiscounts: !combineDiscountWith.orderDiscounts,
                });
              }}
              size="xs"
              value={combineDiscountWith.orderDiscounts}
            />
            {/* Hide "Shipping discounts" option for Free Shipping discounts - combining shipping with shipping doesn't make sense */}
            {discountSettingsType !==
              DiscountSettingsType.SHOPIFY_FREE_SHIPPING && (
              <RedoCheckbox
                label="Shipping discounts"
                setValue={() => {
                  setCombineDiscountWith({
                    ...combineDiscountWith,
                    shippingDiscount: !combineDiscountWith.shippingDiscount,
                  });
                }}
                size="xs"
                value={combineDiscountWith.shippingDiscount}
              />
            )}
          </Flex>
        </LabeledInput>
        {(category !== DiscountCategory.RECOVER || showHiddenRecoverFields) &&
          discountSettingsType === DiscountSettingsType.SHOPIFY_BASIC && (
            <Flex mt="lg">
              <LabeledInput
                label={
                  <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                    Item restrictions
                  </Text>
                }
                size={LabelSize.EXTRA_SMALL}
              >
                <Flex dir="column" gap="md">
                  {!specifyCollections && !specifyProducts && (
                    <RedoCheckbox
                      description="This will create a collection in Shopify to keep track of which items are not on sale."
                      label="Exclude on-sale items"
                      setValue={(checked) => {
                        handleSetExcludeOnSale(checked === true);
                      }}
                      size="xs"
                      value={excludeOnSale}
                    />
                  )}
                  {!excludeOnSale && !specifyProducts && (
                    <RedoCheckbox
                      description="If enabled, the discount will only be applied to items in the specified collections."
                      label="Limit to collections"
                      setValue={(checked) => {
                        handleSetSpecifyCollections(checked === true);
                      }}
                      size="xs"
                      value={specifyCollections}
                    />
                  )}
                  {!excludeOnSale && !specifyCollections && (
                    <RedoCheckbox
                      description="If enabled, the discount will only be applied to the specified products."
                      label="Limit to products"
                      setValue={handleSetSpecifyProducts}
                      size="xs"
                      value={specifyProducts}
                    />
                  )}
                </Flex>

                {specifyCollections && (
                  <CollectionsPicker
                    collections={collectionIds}
                    setCollections={setCollectionIds}
                  />
                )}
                {specifyProducts && (
                  <Flex mt="md">
                    <ProductsPicker
                      products={selectedProducts}
                      setProducts={setSelectedProducts}
                    />
                  </Flex>
                )}
              </LabeledInput>
            </Flex>
          )}

        {!isDynamicRange && (
          <LabeledInput
            label={
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                Expiration
              </Text>
            }
            size={LabelSize.EXTRA_SMALL}
          >
            {category === DiscountCategory.RECOVER ? (
              <Flex dir="column" gap="xs">
                <Text fontSize="sm" textColor="secondary">
                  10 days - fixed duration for Recover discounts
                </Text>
              </Flex>
            ) : (
              <RedoRadioGroup<ExpirationType>
                options={[
                  { id: ExpirationType.NEVER, label: "Never" },
                  ...(codeGenerationStrategy === DiscountGroupType.DYNAMIC
                    ? [{ id: ExpirationType.EXPIRATION_DAYS, label: "Days" }]
                    : []),
                  { id: ExpirationType.DATE, label: "Date" },
                ]}
                setValue={(value) => {
                  setExpiration(getDefaultExpiration(value));
                }}
                size="xs"
                value={expiration.expirationType}
              />
            )}
          </LabeledInput>
        )}

        {!isDynamicRange &&
          category !== DiscountCategory.RECOVER &&
          expiration.expirationType === ExpirationType.EXPIRATION_DAYS && (
            <Flex>
              <RedoTextInput
                inputTypeHint="number"
                label="Number of days"
                min={1}
                placeholder="Enter days"
                setValue={(value) =>
                  setExpiration({ ...expiration, days: Number(value) })
                }
                step={1}
                value={expiration.days?.toString() || ""}
              />
            </Flex>
          )}

        {!isDynamicRange &&
          category !== DiscountCategory.RECOVER &&
          expiration.expirationType === ExpirationType.DATE && (
            <Flex>
              <RedoSingleDatePicker
                buttonSize="sm"
                date={dateToCurrentPlainDate(expiration.date)}
                setDate={(date) =>
                  setExpiration({
                    ...expiration,
                    date: plainDateToCurrentDate(date),
                  })
                }
              />
            </Flex>
          )}

        {/* Start date hidden for DYNAMIC_RANGE: codes are generated on-demand and should be immediately active.
            The startsAt field is still wired through to Shopify in get-code-for-discount.ts if needed in the future. */}
        {category !== DiscountCategory.RECOVER && !isDynamicRange && (
          <LabeledInput
            label={
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                Start date & time (optional)
              </Text>
            }
            size={LabelSize.EXTRA_SMALL}
          >
            <Flex align="center" gap="sm">
              <RedoSingleDatePicker
                buttonSize="sm"
                date={startDate ? dateToCurrentPlainDate(startDate) : undefined}
                setDate={(date) => {
                  const newDate = date
                    ? plainDateToCurrentDate(date)
                    : undefined;
                  setStartDate(newDate);
                  if (!newDate && startTime) {
                    setStartTime(INITIAL_START_TIME);
                  }
                }}
              />
              <RedoTimePicker
                disabled={!startDate}
                setValue={setStartTime}
                size="sm"
                value={startTime}
              />
            </Flex>
          </LabeledInput>
        )}
        {discountSettingsType !== DiscountSettingsType.SHOPIFY_BXGY &&
          (category !== DiscountCategory.RECOVER ||
            showHiddenRecoverFields) && (
            <>
              <RedoTextInput
                fullWidth
                inputTypeHint="number"
                label="Minimum quantity of items bought"
                placeholder="0"
                setValue={(value) => {
                  handleSetMinimumQuantity(value ? Number(value) : undefined);
                }}
                value={minimumQuantity?.toString() ?? ""}
              />
              <RedoTextInput
                fullWidth
                inputTypeHint="number"
                label="Minimum purchase amount"
                placeholder="0.00"
                setValue={(value) => {
                  handleSetMinimumSubtotal(value ? Number(value) : undefined);
                }}
                trailingActions={
                  <Text fontSize="sm" textColor="secondary">
                    $
                  </Text>
                }
                value={minimumSubtotal?.toString() ?? ""}
              />
            </>
          )}

        {category !== DiscountCategory.RECOVER && (
          <>
            <LabeledInput
              label={
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Customer eligibility
                </Text>
              }
              size={LabelSize.EXTRA_SMALL}
            >
              <RedoRadioGroup<CustomerEligibility>
                options={[
                  { id: CustomerEligibility.ALL, label: "All customers" },
                  {
                    id: CustomerEligibility.SEGMENTS,
                    label: "Specific customer segments",
                  },
                  {
                    id: CustomerEligibility.SPECIFIC,
                    label: "Specific customers",
                  },
                ]}
                setValue={setCustomerEligibility}
                size="xs"
                value={customerEligibility}
              />
            </LabeledInput>

            {customerEligibility === CustomerEligibility.SPECIFIC && (
              <LabeledInput
                label={
                  <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                    Select specific customers
                  </Text>
                }
                size={LabelSize.EXTRA_SMALL}
              >
                <CustomerSearchDropdown
                  selectedCustomerIds={selectedCustomerIds}
                  setSelectedCustomerIds={(customerIds) => {
                    setSelectedCustomerIdsString(customerIds.join(", "));
                  }}
                />
              </LabeledInput>
            )}

            {customerEligibility === CustomerEligibility.SEGMENTS && (
              <LabeledInput
                label={
                  <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                    Select customer segments
                  </Text>
                }
                size={LabelSize.EXTRA_SMALL}
              >
                <RedoTagsDropdownInput
                  options={ShopifySegmentOptions}
                  placeholder="Select customer segments"
                  searchPlaceholder="Search segments"
                  selectedOptions={selectedShopifySegmentOptions}
                  setSelectedOptions={handleShopifySegmentChange}
                  size="xs"
                />
              </LabeledInput>
            )}
          </>
        )}

        {category !== DiscountCategory.RECOVER && (
          <>
            <RedoTextInput
              fullWidth
              inputTypeHint="number"
              label="Total number of uses"
              placeholder="Leave empty for unlimited"
              setValue={(value) =>
                setTotalUsageLimit(value ? Number(value) : undefined)
              }
              value={totalUsageLimit?.toString() ?? ""}
            />
            <RedoCheckbox
              label="Limit to 1 use per customer"
              setValue={(value) => setAppliesOncePerCustomer(value === true)}
              size="xs"
              value={appliesOncePerCustomer}
            />
          </>
        )}

        {category === DiscountCategory.RECOVER && (
          <RedoCheckbox
            label="Show hidden fields"
            setValue={(value) => setShowHiddenRecoverFields(value === true)}
            size="xs"
            value={showHiddenRecoverFields}
          />
        )}
      </Flex>
    </RedoModal>
  );
});

const discountValueTypeItems: RedoListItem<DiscountValueType>[] = [
  {
    id: DiscountValueType.PERCENTAGE,
    type: "text",
    text: "%",
    value: DiscountValueType.PERCENTAGE,
  },
  {
    id: DiscountValueType.AMOUNT,
    type: "text",
    text: "$",
    value: DiscountValueType.AMOUNT,
  },
];

const prettyDiscountType = (type: DiscountGroupType) => {
  switch (type) {
    case DiscountGroupType.DYNAMIC:
      return "Dynamic";
    case DiscountGroupType.STATIC:
      return "Static";
    default:
      assertNever(type);
  }
};

const discountGroupTypeOptions: RedoListItem<DiscountGroupType>[] =
  Object.values(DiscountGroupType).map((type) => ({
    id: type,
    type: "text",
    value: type,
    text: prettyDiscountType(type),
  }));

const dynamicRangeExpirationUnitsOptions: RedoListItem<DynamicRangeExpirationUnits>[] =
  [
    { id: DynamicRangeExpirationUnits.MINUTES, text: "Minutes" },
    { id: DynamicRangeExpirationUnits.HOURS, text: "Hours" },
    { id: DynamicRangeExpirationUnits.DAYS, text: "Days" },
    { id: DynamicRangeExpirationUnits.WEEKS, text: "Weeks" },
  ].map((item) => ({ ...item, type: "text" as const, value: item.id }));

const countryOptions: BadgeRedoListItem<{ code: CountryCode; name: string }>[] =
  countries.map((country) => ({
    id: country.code,
    value: { code: country.code, name: country.name },
    type: "badge" as const,
    badge: { text: country.name },
  }));

const countrySearcher = new Fuse<
  BadgeRedoListItem<{ code: CountryCode; name: string }>
>(countryOptions, { keys: ["value.name", "value.code"] });

const searcher = new Fuse<BadgeRedoListItem<{ id: string; name: string }>>([], {
  keys: ["value.name"],
});

const CollectionsPicker = memo(function CollectionsPicker({
  collections,
  setCollections,
}: {
  collections: string[];
  setCollections: (c: string[]) => void;
}) {
  const [collectionsLoad] = useLazyContext(CollectionsContext);

  const [collectionsSearchString, setCollectionsSearchString] =
    useState<string>("");

  const allCollectionOptions: BadgeRedoListItem<{
    id: string;
    name: string;
  }>[] = useMemo(() => {
    const loadedCollections = collectionsLoad.value || [];
    return loadedCollections.map((collection) => ({
      id: collection.id,
      value: collection,
      type: "badge",
      badge: { text: collection.name },
    }));
  }, [collectionsLoad]);

  const dropdownOptions = useSearch(
    searcher,
    allCollectionOptions,
    collectionsSearchString,
  );

  const selectedOptions: BadgeRedoListItem<{ id: string; name: string }>[] =
    useMemo(() => {
      return allCollectionOptions.filter((collection) =>
        collections.includes(collection.value.id),
      );
    }, [allCollectionOptions, collections]);

  const allSelected =
    allCollectionOptions.length > 0 &&
    collections.length === allCollectionOptions.length;

  const handleSelectAll = useHandler((checked: boolean | string) => {
    if (checked === true) {
      setCollections(allCollectionOptions.map((c) => c.value.id));
    } else {
      setCollections([]);
    }
  });

  return (
    <Flex dir="column" gap="sm" mt="sm">
      <RedoCheckbox
        label="Select all collections"
        setValue={handleSelectAll}
        size="xs"
        value={allSelected}
      />
      <RedoTagsDropdownInput
        options={dropdownOptions}
        placeholder="Select collections"
        searchPlaceholder="Search collections"
        searchString={collectionsSearchString}
        selectedOptions={selectedOptions}
        setSearchString={setCollectionsSearchString}
        setSelectedOptions={(items) => {
          setCollections(items.map((item) => item.value.id));
        }}
        size="xs"
      />
    </Flex>
  );
});

const CountriesPicker = memo(function CountriesPicker({
  countryCodes,
  setCountryCodes,
}: {
  countryCodes: string[];
  setCountryCodes: (codes: string[]) => void;
}) {
  const [searchString, setSearchString] = useState("");

  const filteredOptions = useSearch(
    countrySearcher,
    countryOptions,
    searchString,
  );

  const selectedOptions: BadgeRedoListItem<{
    code: CountryCode;
    name: string;
  }>[] = useMemo(() => {
    return countryOptions.filter((option) =>
      countryCodes.includes(option.value.code),
    );
  }, [countryCodes]);

  return (
    <RedoTagsDropdownInput
      options={filteredOptions}
      placeholder="All countries (leave empty for all)"
      searchPlaceholder="Search countries"
      searchString={searchString}
      selectedOptions={selectedOptions}
      setSearchString={setSearchString}
      setSelectedOptions={(items) => {
        setCountryCodes(items.map((item) => item.value.code));
      }}
      size="xs"
    />
  );
});

export function convertProductInfoToModalProduct(
  product: ProductInfo,
): ModalProduct {
  return {
    id: product.id,
    title: product.name,
    description: product.description || "",
    handle: "",
    image: {
      src: product.image?.url || null,
      altText: product.image?.alt || null,
    },
    images: product.image?.url
      ? [{ src: product.image.url, altText: product.image.alt || null }]
      : [],
    tags: product.tags || [],
    variants: product.variants.map((variant) => ({
      admin_graphql_api_id: variant.id,
      inventory_quantity: variant.quantityAvailable || 0,
      title: variant.name,
      price: variant.price.toString(),
      image: {
        src: variant.image?.url || null,
        altText: variant.image?.alt || null,
      },
      productTitle: product.name,
    })),
  };
}

const discountCodeNameTitle: Record<DiscountGroupType, string> = {
  [DiscountGroupType.STATIC]: "Discount code",
  [DiscountGroupType.DYNAMIC]: "Discount code prefix",
};

function getDiscountCodeStrategyDescription({
  prefix,
  type,
}: {
  prefix: string;
  type: DiscountGroupType;
}) {
  switch (type) {
    case DiscountGroupType.STATIC:
      return `The code ${prefix.toUpperCase()} will be sent to all customers.`;
    case DiscountGroupType.DYNAMIC:
      return `A unique discount code with this prefix will be generated for each customer. Eg '${prefix.toUpperCase()}-12345'`;
    default:
      assertNever(type);
  }
}

function getDefaultExpiration(expirationType: ExpirationType) {
  switch (expirationType) {
    case ExpirationType.NEVER:
    case ExpirationType.DELIVERY:
      return { expirationType };
    case ExpirationType.EXPIRATION_DAYS:
      return { expirationType, days: 7 };
    case ExpirationType.DATE:
      return { expirationType, date: new Date() };
    default:
      assertNever(expirationType);
  }
}
